import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Box,
    Card,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import IfCondition from 'components/shared/IfCondition';
import useGeneralDialog from 'hooks/dialogs/useGeneralDialog';
import React, { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils/utils';
import useRegistration from '../../hooks/authentication/useRegistration';
import useDefaultStockSWR from '../../hooks/useDefaultStock';
import useFullScreenLoading from '../../hooks/useFullScreenLoading';
import {
    RegistrationComponentsProps,
    RegistrationRequest,
} from '../../models/Authentication/Registration';
import SnackbarUtils from '../../utils/SnackbarUtils';
import {
    B2B_URL,
    LEGAL_SITE_URLS,
    PATTERNS,
} from '../../utils/constans/constans';
import PrimaryBtn from '../shared/Buttons/PrimaryButton';
import SelectStyled from '../shared/SelectStyled';
import TextFieldStyled from '../shared/TextFieldStyled';
import { AuthPageTitle } from '../shared/Typography/AuthenticationsPageTitle';
import { FormControlsWrapper } from '../styled/Form/FormControlsWrapper';
import { SubmitButtonWrapper } from '../styled/Form/SubmitButtonWrapper';
import InvalidTEAORWarningContent from './InvalidTEAORWarningContent';
import ListOfTEAORNumbers from './ListOfTEAORNumbers';

export const RegistrationForm: React.FC<RegistrationComponentsProps> = (
    props
) => {
    const {
        register,
        handleSubmit,
        watch,
        getValues,
        formState: { errors },
        trigger,
        resetField,
    } = useForm<RegistrationRequest>({
        mode: 'onChange',
    });
    const { handleStateChange } = props;
    const termAndConditions = B2B_URL + LEGAL_SITE_URLS.TERMS_AND_CONDITIONS;
    const privacyStatement = B2B_URL + LEGAL_SITE_URLS.PRIVACY_STATEMENT;
    const fullScreenLoading = useFullScreenLoading();

    const theme = useTheme();

    //get default stock list
    const { stocks, isLoadingStock, isErrorStock } = useDefaultStockSWR();
    const registration = useRegistration();
    const { t } = useTranslation();
    const { t: tError } = useTranslation('errors', { keyPrefix: 'ERROR' });

    const navigate = useNavigate();
    //handle default stock change
    const [defaultStock, setDefaultStock] = useState('');
    const [country, setCountry] = useState('HU');

    const handleChange = (event: SelectChangeEvent) => {
        setDefaultStock(event.target.value as string);
    };
    const handleCountryChange = (event: SelectChangeEvent) => {
        setCountry(event.target.value as string);
    };

    const [isLoading, setIsLoading] = useState(false);

    const [canContinue, setCanContinue] = useState<boolean>(false);

    const generalDialog = useGeneralDialog();

    //handle password match
    const password = useRef({});
    password.current = watch('password', '');

    const onSubmit: SubmitHandler<RegistrationRequest> = (data) => {
        setIsLoading(true);
        fullScreenLoading.setFullScreenLoading(true);

        registration
            .sendRegistration(data)
            .then((): void => {
                SnackbarUtils.success(
                    t('NOTIFICATION.REGISTRATION_SUCCESS').toString()
                );

                navigate('/login', { replace: true });
            })
            .catch((error) => console.log(error))
            .finally((): void => {
                fullScreenLoading.setFullScreenLoading(false);
                setIsLoading(false);
            });
    };

    const showInvalidTEAORDialog = () => {
        generalDialog.showDialog({
            title: tError('REGISTRATION_ERRORS.TAX_NUMBER.INVALID_TEAOR.TITLE'),
            closeLabel: tError(
                'REGISTRATION_ERRORS.TAX_NUMBER.INVALID_TEAOR.CLOSE'
            ),
            component: InvalidTEAORWarningContent,
            onCloseWithSuccess: () => generalDialog.closeDialog(),
        });
    };

    const getCompanyDetails = () => {
        trigger(['email', 'tax_number']).then((valid: boolean): void => {
            if (!valid) return;

            fullScreenLoading.setFullScreenLoading(true);

            registration
                .getCompanyInfoFromVat({
                    vat: getValues('tax_number'),
                    email: getValues('email'),
                })
                .then((data) => {
                    if (isNullOrUndefined(data)) return;

                    switch (data.status) {
                        case 'Valid':
                            resetField('postcode', {
                                defaultValue: data.verifiedPostCode,
                                keepDirty: true,
                            });

                            resetField('city', {
                                defaultValue: data.verifiedCity,
                                keepDirty: true,
                            });

                            resetField('address1', {
                                defaultValue:
                                    data.verfiedStreet +
                                    ' ' +
                                    data.verifiedPublicPremise +
                                    ' ' +
                                    data.verifiedHouseNo,
                                keepDirty: true,
                            });

                            resetField('company', {
                                defaultValue: isNullOrUndefinedOrEmpty(
                                    data.verifiedShortName
                                )
                                    ? data.verifiedName
                                    : data.verifiedShortName,
                                keepDirty: true,
                            });

                            if (!data.validTEAOR) {
                                showInvalidTEAORDialog();
                                return;
                            }

                            setCanContinue(true);
                            break;
                        default:
                            return SnackbarUtils.error(
                                t('REGISTRATION_FORM_FIELDS.AUTO_FILL_ERROR')
                            );
                    }
                })
                .catch((error) => console.log(error))
                .finally(() => {
                    fullScreenLoading.setFullScreenLoading(false);
                    setIsLoading(false);
                });
        });
    };

    if (isLoadingStock) {
        return <></>;
    }

    if (isErrorStock) {
        return <>{t('NOTIFICATION.ERROR_LOADING')}</>;
    }

    return (
        <>
            <AuthPageTitle>
                {t('REGISTRATION_FORM_FIELDS.REGISTRATION').toString()}
            </AuthPageTitle>
            <Typography paragraph>
                {t(
                    'REGISTRATION_FORM_FIELDS.REGISTRATION_SHORT_DESC'
                ).toString()}
            </Typography>
            <Typography paragraph>
                {t('REGISTRATION_FORM_FIELDS.HAVE_ACCOUNT').toString()}{' '}
                <Link to="/login">
                    {' '}
                    {t('REGISTRATION_FORM_FIELDS.HAVE_ACCOUNT_LINK').toString()}
                </Link>
            </Typography>
            <Typography paragraph>
                {t('REGISTRATION_FORM_FIELDS.HAVE_ACCOUNT_IN_BC').toString()}{' '}
                <Box
                    component={'span'}
                    sx={{ color: '#007cb0', cursor: 'pointer' }}
                    onClick={() => handleStateChange('exist')}
                >
                    {' '}
                    {t(
                        'REGISTRATION_FORM_FIELDS.HAVE_ACCOUNT_IN_BC_LINK'
                    ).toString()}
                </Box>
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Typography paragraph>
                    {t('REGISTRATION_FORM_FIELDS.PROFILE_FORMS').toString()}
                </Typography>
                {/* Email address */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        onChangeCapture={() =>
                            canContinue && setCanContinue(false)
                        }
                        label={t('REGISTRATION_FORM_FIELDS.EMAIL').toString()}
                        error={!!errors.email}
                        {...register('email', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            pattern: {
                                value: PATTERNS.VALID_EMAIL,
                                message: t(
                                    'FORM_VALIDATION_ERROR.EMAIL'
                                ).toString(),
                            },
                        })}
                        helperText={errors.email ? errors.email.message : ' '}
                    />
                </FormControl>

                {/* Tax number */}
                <FormControlsWrapper>
                    <FormControl fullWidth>
                        <TextFieldStyled
                            onChangeCapture={() =>
                                canContinue && setCanContinue(false)
                            }
                            label={t(
                                'REGISTRATION_FORM_FIELDS.TAX_NUMBER'
                            ).toString()}
                            error={!!errors.tax_number}
                            {...register('tax_number', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                                pattern: {
                                    value: PATTERNS.HUN_TAX_NUMBER,
                                    message: t(
                                        'FORM_VALIDATION_ERROR.TAX_NUMBER_FORMAT'
                                    ).toString(),
                                },
                            })}
                            helperText={
                                errors.tax_number
                                    ? errors.tax_number.message
                                    : ' '
                            }
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <PrimaryBtn
                            onClick={getCompanyDetails}
                            disabled={canContinue}
                        >
                            {t('REGISTRATION_FORM_FIELDS.AUTO_FILL')}
                        </PrimaryBtn>
                    </FormControl>
                </FormControlsWrapper>

                <IfCondition condition={!canContinue}>
                    <Stack marginBottom={4}>
                        <Card variant="outlined">
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                gap={2}
                                padding={1.5}
                            >
                                <WarningAmberIcon color="error" />
                                <Stack flexDirection="column">
                                    <Typography variant="caption">
                                        {t(
                                            'REGISTRATION_FORM_FIELDS.MUST_TEST_TITLE'
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="small"
                                        color={theme.palette.grey[600]}
                                        textAlign="justify"
                                    >
                                        {t(
                                            'REGISTRATION_FORM_FIELDS.MUST_TEST_DESCRIPTION'
                                        )}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Card>
                    </Stack>
                </IfCondition>

                {/* Company */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        disabled={!canContinue}
                        label={t('REGISTRATION_FORM_FIELDS.COMPANY').toString()}
                        error={!!errors.company}
                        {...register('company', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                        })}
                        InputLabelProps={{ shrink: !!watch('company') }}
                        helperText={
                            errors.company ? errors.company.message : ' '
                        }
                    />
                </FormControl>
                <FormControlsWrapper>
                    {/* Last Name */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled={!canContinue}
                            label={t(
                                'REGISTRATION_FORM_FIELDS.LAST_NAME'
                            ).toString()}
                            error={!!errors.last_name}
                            {...register('last_name', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                                minLength: {
                                    value: 3,
                                    message: t(
                                        'FORM_VALIDATION_ERROR.NAME_MIN_LENGTH'
                                    ).toString(),
                                },
                            })}
                            helperText={
                                errors.last_name
                                    ? errors.last_name.message
                                    : ' '
                            }
                        />
                    </FormControl>
                    {/* First Name */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled={!canContinue}
                            label={t(
                                'REGISTRATION_FORM_FIELDS.FIRST_NAME'
                            ).toString()}
                            error={!!errors.first_name}
                            {...register('first_name', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                                minLength: {
                                    value: 3,
                                    message: t(
                                        'FORM_VALIDATION_ERROR.NAME_MIN_LENGTH'
                                    ).toString(),
                                },
                            })}
                            helperText={
                                errors.first_name
                                    ? errors.first_name.message
                                    : ' '
                            }
                        />
                    </FormControl>
                </FormControlsWrapper>
                {/* Phone */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        disabled={!canContinue}
                        label={t('REGISTRATION_FORM_FIELDS.PHONE').toString()}
                        error={!!errors.phone}
                        {...register('phone', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            pattern: {
                                value: PATTERNS.PHONE_NUMBER,
                                message: t(
                                    'FORM_VALIDATION_ERROR.PHONE'
                                ).toString(),
                            },
                        })}
                        helperText={errors.phone ? errors.phone.message : ' '}
                    />
                </FormControl>
                {/* Default stock */}
                <FormControl fullWidth variant="filled">
                    <InputLabel
                        sx={{
                            color: '#999999',
                        }}
                    >
                        {t('REGISTRATION_FORM_FIELDS.DEFAULT_STOCK').toString()}
                    </InputLabel>
                    <SelectStyled
                        disabled={!canContinue}
                        label={t(
                            'REGISTRATION_FORM_FIELDS.DEFAULT_STOCK'
                        ).toString()}
                        {...register('default_stock', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            onChange: (e) => handleChange(e),
                        })}
                        value={defaultStock}
                    >
                        {stocks.map((stock) => (
                            <MenuItem
                                key={stock.bc_location_code}
                                value={stock.bc_location_code}
                            >
                                {stock.bc_location_name}
                            </MenuItem>
                        ))}
                    </SelectStyled>
                    <FormHelperText error={!!errors.default_stock}>
                        &#8203;
                        {errors.default_stock && errors.default_stock.message}
                    </FormHelperText>
                </FormControl>
                <Typography paragraph>
                    {t(
                        'REGISTRATION_FORM_FIELDS.DEFAULT_SHIPPING_FORMS'
                    ).toString()}
                </Typography>
                {/* Country */}
                <FormControl fullWidth variant="filled">
                    <InputLabel
                        sx={{
                            color: '#999999',
                        }}
                    >
                        {t('REGISTRATION_FORM_FIELDS.COUNTRY').toString()}
                    </InputLabel>
                    <SelectStyled
                        disabled={!canContinue}
                        label={t('REGISTRATION_FORM_FIELDS.COUNTRY').toString()}
                        {...register('country', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            onChange: (e) => handleCountryChange(e),
                        })}
                        value={country}
                    >
                        <MenuItem value={'HU'}>
                            {t('COUNTRIES.HU', { ns: 'countries' })}
                        </MenuItem>
                    </SelectStyled>
                    <FormHelperText error={!!errors.country}>
                        &#8203;
                        {errors.country && errors.country.message}
                    </FormHelperText>
                </FormControl>
                <FormControlsWrapper>
                    {/* Postcode */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled={!canContinue}
                            label={t(
                                'REGISTRATION_FORM_FIELDS.POSTAL_CODE'
                            ).toString()}
                            error={!!errors.postcode}
                            {...register('postcode', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                            })}
                            InputLabelProps={{ shrink: !!watch('postcode') }}
                            helperText={
                                errors.postcode ? errors.postcode.message : ' '
                            }
                        />
                    </FormControl>
                    {/* City */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled={!canContinue}
                            label={t(
                                'REGISTRATION_FORM_FIELDS.CITY'
                            ).toString()}
                            error={!!errors.city}
                            {...register('city', {
                                required: t(
                                    'FORM_VALIDATION_ERROR.REQUIRED'
                                ).toString(),
                            })}
                            InputLabelProps={{ shrink: !!watch('city') }}
                            helperText={errors.city ? errors.city.message : ' '}
                        />
                    </FormControl>
                </FormControlsWrapper>
                {/* Address 1 */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        disabled={!canContinue}
                        label={t(
                            'REGISTRATION_FORM_FIELDS.ADDRESS1'
                        ).toString()}
                        error={!!errors.address1}
                        {...register('address1', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                        })}
                        InputLabelProps={{ shrink: !!watch('address1') }}
                        helperText={
                            errors.address1 ? errors.address1.message : ' '
                        }
                    />
                </FormControl>
                {/* Adress 2 */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        disabled={!canContinue}
                        label={t(
                            'REGISTRATION_FORM_FIELDS.ADDRESS2'
                        ).toString()}
                        InputLabelProps={{ shrink: !!watch('address2') }}
                        {...register('address2')}
                        helperText={' '}
                    />
                </FormControl>
                {/* Password */}
                <Typography paragraph>
                    {t('REGISTRATION_FORM_FIELDS.PASSWORD_FORMS').toString()}
                </Typography>
                <FormControl fullWidth>
                    <TextFieldStyled
                        disabled={!canContinue}
                        label={t(
                            'REGISTRATION_FORM_FIELDS.PASSWORD'
                        ).toString()}
                        error={!!errors.password}
                        type="password"
                        {...register('password', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            minLength: {
                                value: 5,
                                message: t(
                                    'FORM_VALIDATION_ERROR.PASSWORD_MIN_LENGTH'
                                ).toString(),
                            },
                        })}
                        helperText={
                            errors.password ? errors.password.message : ' '
                        }
                    />
                </FormControl>
                {/* Retry Password */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        disabled={!canContinue}
                        label={t(
                            'REGISTRATION_FORM_FIELDS.RETRY_PASSWORD'
                        ).toString()}
                        error={!!errors.password_confirmation}
                        type="password"
                        {...register('password_confirmation', {
                            required: t(
                                'FORM_VALIDATION_ERROR.REQUIRED'
                            ).toString(),
                            validate: (value) =>
                                value === password.current ||
                                t(
                                    'FORM_VALIDATION_ERROR.PASSWORD_MISMATCH'
                                ).toString(),
                        })}
                        helperText={
                            errors.password_confirmation
                                ? errors.password_confirmation.message
                                : ' '
                        }
                    />
                </FormControl>
                {/* Note */}
                <Typography paragraph>
                    {t('REGISTRATION_FORM_FIELDS.NOTE').toString()}
                </Typography>
                <FormControl fullWidth>
                    <TextFieldStyled
                        disabled={!canContinue}
                        label={t(
                            'REGISTRATION_FORM_FIELDS.NOTE_DESC'
                        ).toString()}
                        multiline
                        rows={4}
                        {...register('note')}
                    />
                </FormControl>
                {/* Terms of service */}
                <FormControl fullWidth>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={!canContinue}
                                {...register('terms_of_service', {
                                    required: t(
                                        'FORM_VALIDATION_ERROR.REQUIRED'
                                    ).toString(),
                                })}
                            />
                        }
                        label={
                            <>
                                <p>
                                    {t(
                                        'REGISTRATION_FORM_FIELDS.TERMS_OF_SERVICE'
                                    ).toString()}
                                    <a
                                        href={termAndConditions}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {t(
                                            'REGISTRATION_FORM_FIELDS.TERMS_OF_SERVICE_LINK'
                                        ).toString()}
                                    </a>
                                    {t(
                                        'REGISTRATION_FORM_FIELDS.AND'
                                    ).toString()}
                                    <a
                                        href={privacyStatement}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {t(
                                            'REGISTRATION_FORM_FIELDS.ASZF'
                                        ).toString()}
                                    </a>
                                </p>
                            </>
                        }
                    />
                    <FormHelperText error={!!errors.terms_of_service}>
                        {errors.terms_of_service &&
                            errors.terms_of_service.message}
                    </FormHelperText>
                </FormControl>
                {/* Has 4322 */}
                <FormControl fullWidth>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={!canContinue}
                                {...register('has4322', {
                                    required: t(
                                        'FORM_VALIDATION_ERROR.REQUIRED'
                                    ).toString(),
                                })}
                            />
                        }
                        label={
                            <p>
                                {t('REGISTRATION_FORM_FIELDS.HAS4322', {
                                    returnObjects: true,
                                }).toString()}
                            </p>
                        }
                    />
                    <Box
                        sx={{
                            paddingLeft: '32px',
                            marginTop: '-12px',
                        }}
                    >
                        <ListOfTEAORNumbers />
                    </Box>
                    <FormHelperText error={!!errors.has4322}>
                        {errors.has4322 && errors.has4322.message}
                    </FormHelperText>
                </FormControl>
                {/* Submit */}
                <SubmitButtonWrapper>
                    <PrimaryBtn
                        type="submit"
                        disabled={isLoading || !canContinue}
                    >
                        {t('REGISTRATION_FORM_FIELDS.REGISTRATION').toString()}
                    </PrimaryBtn>
                </SubmitButtonWrapper>
            </form>
        </>
    );
};
